/* =============================================================================
   Storm Light — theme behaviours.

   Progressive enhancement throughout: every one of these works without JS.
   The drawer is a <dialog>, the accordions are <details>, the hero shows its
   poster. Nothing here is required to shop.

   No frameworks. Custom elements, matching Horizon's convention.
   ============================================================================= */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------- ws-drawer */
/* Menu / cart / filter drawer. Native <dialog> gives us the focus trap, the
   Esc key and the top layer for free — hand-rolling those is where custom
   drawers usually get accessibility wrong. */
class WSDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.opener = document.querySelector(`[data-ws-drawer-open="${this.id}"]`);
    if (!this.dialog) return;

    // The cart opener is an <a href="/cart"> so the cart is reachable with JS
    // off. With JS on it must NOT navigate, or the drawer opens and the page
    // immediately leaves — which is exactly what it did: the cart drawer
    // rendered on every page and could never be seen.
    this.opener?.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });
    this.querySelectorAll('[data-ws-drawer-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );

    // Click on the backdrop (the dialog element itself, outside its panel).
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    // TEARDOWN IS EXPLICIT, NOT EVENT-DRIVEN.
    //
    // The obvious implementation hangs teardown on the dialog's `close` event.
    // Measured in this storefront, `close` NEVER FIRES — not from our close(),
    // not from calling dialog.close() directly on a modally-opened dialog.
    // Something in the page patches it. The symptom was ugly and silent: the
    // drawer shut, but `overflow: hidden` stayed on <html>, so the page could
    // no longer scroll, and aria-expanded stayed "true" so a screen reader kept
    // announcing the cart as open.
    //
    // So every closing path calls #teardown() itself, and the events are kept
    // only as a backstop for closes we did not initiate. Teardown is idempotent.
    this.dialog.addEventListener('close', () => {
      // A queued close event may arrive after this drawer has reopened.
      if (!this.dialog.open) this.teardown();
    });
    this.dialog.addEventListener('cancel', () => this.teardown());   // Esc
  }

  open() {
    this.cancelClose();
    // showModal() throws InvalidStateError on an already-open dialog, which
    // happens the moment someone adds a second item while the cart drawer is
    // showing. The throw would abort the rest of the add handler.
    if (!this.dialog.open) this.dialog.showModal();

    // showModal focuses the first tabbable node, which is the close button —
    // fine, but a screen reader then hears "close" before it hears what opened.
    // Focusing the panel announces the drawer's own label first.
    const panel = this.dialog.querySelector('.ws-drawer__panel');
    if (panel) {
      panel.setAttribute('tabindex', '-1');
      panel.focus();
    }
    this.opener?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    this.dialog.removeAttribute('data-closing');
  }

  teardown() {
    this.cancelClose();
    this.opener?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
  }

  close() {
    if (!this.dialog.open) return this.teardown();
    // Let the slide-out play before the dialog is removed from the top layer.
    if (reduceMotion.matches) {
      this.dialog.close();
      return this.teardown();
    }
    // Repeated close requests share one timer; reopening cancels it.
    if (this.closeTimer) return;
    this.dialog.setAttribute('data-closing', 'true');
    this.closeTimer = setTimeout(() => {
      this.dialog.close();
      this.teardown();
    }, 240);
  }
}
customElements.define('ws-drawer', WSDrawer);

/* ---------------------------------------------------------------- ws-header */
/* Hides on scroll down, reveals on scroll up. Mobile only — on desktop the
   header is short enough that hiding it just costs a click. */
class WSHeader extends HTMLElement {
  connectedCallback() {
    // WHICH THING ACTUALLY SCROLLS.
    //
    // Above 990px Horizon makes `.page-wrapper` the scroll container and pins
    // html and body to 100dvh — that is deliberate, it is what lets the drawers
    // slide the page sideways. The consequence is that window.scrollY is
    // permanently 0 on desktop, so a listener on `window` never fires there.
    //
    // Hiding the header is mobile-only by design (below), so that part was
    // unaffected. But the hairline the header grows once you have scrolled rode
    // the same listener, and never appeared on desktop at all.
    this.scroller = this.pickScroller();
    this.last = this.scrollTop();
    this.threshold = 64;
    this.ticking = false;

    const onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    };
    this.scroller.addEventListener('scroll', onScroll, { passive: true });

    // The scroll container changes at the breakpoint, so re-pick on resize.
    window.addEventListener('resize', () => {
      const next = this.pickScroller();
      if (next === this.scroller) return;
      this.scroller.removeEventListener('scroll', onScroll);
      this.scroller = next;
      this.scroller.addEventListener('scroll', onScroll, { passive: true });
      this.update();
    }, { passive: true });
  }

  pickScroller() {
    const pw = document.querySelector('.page-wrapper');
    return pw && pw.scrollHeight > pw.clientHeight ? pw : window;
  }

  scrollTop() {
    return this.scroller === window ? window.scrollY : this.scroller.scrollTop;
  }

  update() {
    const y = this.scrollTop();
    // Never hide while a menu is open, or while focus is inside the header —
    // WCAG 2.2 Focus Not Obscured.
    const focusInside = this.contains(document.activeElement);

    if (y > this.threshold && y > this.last && !focusInside) {
      this.setAttribute('data-hidden', 'true');
    } else {
      this.removeAttribute('data-hidden');
    }
    this.toggleAttribute('data-scrolled', y > 8);
    this.last = y;
    this.ticking = false;
  }
}
customElements.define('ws-header', WSHeader);

/* ---------------------------------------------------------------- ws-reveal */
/* Reveals disclose content; they never decorate. Runs once, never re-animates,
   and does nothing at all under reduced motion. */
class WSReveal extends HTMLElement {
  connectedCallback() {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      this.markAll();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-revealed', 'true');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });

    this.querySelectorAll('.ws-reveal').forEach((el, i) => {
      // Stagger a maximum of three children; beyond that it reads as a queue.
      // 80ms cadence matches --ws-reveal-stagger (P5b heavier motion). Kept in
      // sync deliberately; the token drives CSS, this drives the JS delay.
      if (i < 3) el.style.transitionDelay = `${i * 80}ms`;
      io.observe(el);
    });
  }

  markAll() {
    this.querySelectorAll('.ws-reveal').forEach((el) =>
      el.setAttribute('data-revealed', 'true')
    );
  }
}
customElements.define('ws-reveal', WSReveal);
/* Fail-open gate. Only NOW may the hidden state apply: ws-base.css hides
   .ws-reveal under .ws-reveal-ready, not .js. If anything above this line threw —
   a parse error, an unsupported API, a bug in an earlier module — this class is
   never added and every .ws-reveal stays visible instead of stranded at opacity:0. */
document.documentElement.classList.add('ws-reveal-ready');

/* --------------------------------------------------------------- lightbox */
/* Click / tap / Enter on a gallery frame opens a full-screen enlarged view so
   the cloth can be inspected. Pure enhancement: the frames are plain images
   until this upgrades them, so a no-JS gallery loses nothing. Native <dialog>
   gives Esc-to-close and focus trapping for free. */
class WSLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.ws-lightbox');
    this.frames = [...this.querySelectorAll('[data-ws-zoom-src]')];
    if (!this.dialog || typeof this.dialog.showModal !== 'function' || !this.frames.length) return;

    // Fix Pass 1 Codex Claim 1 — the interactive upgrade must be ATOMIC with
    // readiness. Everything below runs inside try/catch; the frames' role/tabindex
    // + open handlers are applied LAST (immediately before .is-ready); and any
    // throw strips the affordances it added and leaves .is-ready off. So a partial
    // init failure degrades to a plain, browsable image strip — never orphaned
    // "button" elements that focus and are AT-announced but do nothing.
    try {
      this.img = this.dialog.querySelector('[data-ws-zoom-img]');
      this.stage = this.dialog.querySelector('.ws-lightbox__stage');
      this.count = this.dialog.querySelector('[data-ws-zoom-count]');
      this.lbThumbs = [...this.dialog.querySelectorAll('[data-ws-lb-thumb]')];
      this.index = 0;

      // Zoom / pan state (P3 item 4).
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.ZOOM = 2.2;

      this.dialog.querySelector('[data-ws-zoom-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.querySelector('[data-ws-zoom-prev]')?.addEventListener('click', () => this.step(-1));
      this.dialog.querySelector('[data-ws-zoom-next]')?.addEventListener('click', () => this.step(1));
      // Click the surrounding dark backdrop (never the image) to dismiss.
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          const stops = [...this.dialog.querySelectorAll('button:not([disabled])')].filter((button) => button.getClientRects().length);
          if (document.activeElement === (e.shiftKey ? stops[0] : stops[stops.length - 1])) {
            e.preventDefault();
            (e.shiftKey ? stops[stops.length - 1] : stops[0])?.focus();
            return;
          }
        }
        if (e.key === 'ArrowLeft') this.step(-1);
        if (e.key === 'ArrowRight') this.step(1);
        // Keyboard parity for zoom: +/- toggle (arrows still step frames).
        if (e.key === '+' || e.key === '=') { e.preventDefault(); this.toggleZoom(); }
        if (e.key === '-' || e.key === '_') { e.preventDefault(); this.zoomOut(); }
      });
      // Zoom never persists across a close or a frame change.
      this.dialog.addEventListener('close', () => this.zoomOut());

      // In-lightbox thumbnail rail: jump straight to a frame, keep place.
      this.lbThumbs.forEach((t, i) => t.addEventListener('click', () => {
        this.index = i;
        this.zoomOut();
        this.render();
      }));

      this.dialog.addEventListener('focusin', (e) => {
        if (this.lbThumbs.includes(e.target) && e.target.matches(':focus-visible')) {
          e.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
        }
      });

      this.wireZoomPan();

      // Interactive upgrade LAST — expose the frames as buttons and bind the
      // openers only once every step above has succeeded, so the affordances and
      // .is-ready land together (or not at all).
      this.frames.forEach((frame, i) => {
        frame.addEventListener('click', () => this.open(i));
        frame.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(i); }
        });
        frame.setAttribute('role', 'button');
        frame.setAttribute('tabindex', '0');
      });

      this.classList.add('is-ready');
    } catch (err) {
      // Fail-open: undo any interactive affordances (never strand role/tabindex
      // without .is-ready) and stay a plain, browsable image strip.
      this.frames.forEach((frame) => {
        frame.removeAttribute('role');
        frame.removeAttribute('tabindex');
      });
      this.classList.remove('is-ready');
    }
  }

  // Click/tap toggles 1x <-> ZOOM; at >1x, pointer- AND touch-drag pan, clamped.
  wireZoomPan() {
    const img = this.img;
    let downX = 0, downY = 0, startTx = 0, startTy = 0, moved = 0, dragging = false;

    img.addEventListener('pointerdown', (e) => {
      if (this.scale <= 1) { moved = 0; return; }   // let pointerup toggle out
      dragging = true;
      moved = 0;
      downX = e.clientX; downY = e.clientY;
      startTx = this.tx; startTy = this.ty;
      img.classList.add('is-panning');
      img.setPointerCapture?.(e.pointerId);
    });

    img.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - downX, dy = e.clientY - downY;
      moved = Math.max(moved, Math.abs(dx) + Math.abs(dy));
      this.tx = startTx + dx;
      this.ty = startTy + dy;
      this.clampPan();
      this.applyTransform();
    });

    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      img.classList.remove('is-panning');
      img.releasePointerCapture?.(e.pointerId);
    };

    img.addEventListener('pointerup', (e) => {
      const wasDrag = dragging && moved > 6;
      end(e);
      if (!wasDrag) this.toggleZoom();   // a tap (little movement) toggles zoom
    });
    img.addEventListener('pointercancel', end);
  }

  toggleZoom() {
    if (this.scale > 1) return this.zoomOut();
    this.scale = this.ZOOM;
    this.tx = 0; this.ty = 0;
    this.clampPan();
    this.applyTransform();
    this.dialog.setAttribute('data-zoomed', '');
  }

  zoomOut() {
    this.scale = 1;
    this.tx = 0; this.ty = 0;
    this.applyTransform();
    this.dialog.removeAttribute('data-zoomed');
  }

  // Harvested from drag-zoom-wrapper.js #constrainTranslation(): object-fit:
  // contain overflow, clamped to half the overflow in each axis. Screen-pixel
  // translate (transform order translate() scale()), so no /scale term; the
  // mobile-only self-limit is dropped (desktop pointer-drag is wanted too).
  clampPan() {
    if (!this.stage) return;
    const rect = this.stage.getBoundingClientRect();
    const nW = this.img.naturalWidth, nH = this.img.naturalHeight;
    if (!nW || !nH || !rect.width || !rect.height) return;
    const cAR = nW / nH, wAR = rect.width / rect.height;
    let dispW, dispH;
    if (cAR > wAR) { dispW = rect.width; dispH = rect.width / cAR; }
    else { dispH = rect.height; dispW = rect.height * cAR; }
    const maxX = Math.max(0, (dispW * this.scale - rect.width) / 2);
    const maxY = Math.max(0, (dispH * this.scale - rect.height) / 2);
    this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
    this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
  }

  applyTransform() {
    this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
  }

  open(i) { this.index = i; this.zoomOut(); this.render(); this.dialog.showModal(); }

  step(d) {
    this.index = (this.index + d + this.frames.length) % this.frames.length;
    this.zoomOut();
    this.render();
  }

  render() {
    const frame = this.frames[this.index];
    this.img.src = frame.dataset.wsZoomSrc;
    this.img.alt = frame.getAttribute('alt') || '';
    if (this.count) this.count.textContent = `${this.index + 1} / ${this.frames.length}`;
    this.lbThumbs.forEach((t, i) => t.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
  }
}
customElements.define('ws-lightbox', WSLightbox);

/* ---------------------------------------------------------------- ws-gallery */
/* P3 items 2, 3, 5 — the PDP gallery controller. Owns the thumbnail rail's
   active state, the desktop hover-zoom, and the scroll/swap to a variant's
   featured frame on swatch change. The rail links are real anchors to each
   frame with JS off (jump-to-frame); this upgrades them to buttons with a
   persistent active ring and, on desktop, an opacity swap of the primary. All
   of it is gated behind the .is-ready class this adds LAST, so if anything here
   throws the gallery stays a plain, browsable scroll strip (fail-open). */
(function gallery() {
  const host = document.querySelector('.ws-pdp__gallery');
  const strip = document.getElementById('ws-gallery');
  const rail = document.querySelector('[data-ws-gallery-rail]');
  if (!host || !strip || !rail) return;

  const slides = [...strip.querySelectorAll('.ws-gallery__slide')];
  const thumbs = [...rail.querySelectorAll('[data-ws-thumb]')];
  if (slides.length < 2) return;

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const desktop = () => desktopQuery.matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  let active = 0;

  const setActive = (i) => {
    if (i < 0 || i >= slides.length) return;
    const focusedSlide = slides.find((slide) => slide.contains(document.activeElement));
    active = i;
    // Fix Pass 1 Blocker 1 — toggleAttribute(name, true) writes data-active=""
    // (empty string), but the desktop stacked-primary CSS, the no-JS Liquid
    // contract, and the lightbox all key on [data-active="true"]. Write the
    // literal "true" string (mirroring the aria-current line) so a frame actually
    // matches and the desktop gallery paints. Do NOT weaken the CSS selector.
    slides.forEach((s, n) => {
      if (n === i) s.setAttribute('data-active', 'true');
      else s.removeAttribute('data-active');
      s.inert = desktop() && n !== i;
    });
    thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
    if (desktop() && focusedSlide && focusedSlide !== slides[i]) {
      (slides[i].querySelector('[data-ws-zoom-src][tabindex]') || thumbs[i])?.focus({ preventScroll: true });
    }
  };

  // Desktop swaps by opacity (the stack). Mobile scrolls the snap strip to the
  // frame. Reduced motion jumps rather than smooth-scrolls.
  const goto = (i) => {
    setActive(i);
    if (!desktop()) {
      strip.scrollTo({ left: slides[i].offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  };

  // Desktop stacks gallery slides, while smaller viewports use a horizontal strip.
  // Keep the selected media in view when the layout crosses that breakpoint.
  desktopQuery.addEventListener('change', (event) => {
    const focusedIndex = slides.findIndex((slide) => slide.contains(document.activeElement));
    setActive(event.matches && focusedIndex >= 0 ? focusedIndex : active);
    if (!event.matches) {
      strip.scrollTo({ left: slides[active].offsetLeft, behavior: 'auto' });
    }
  });

  thumbs.forEach((t, i) => {
    t.setAttribute('role', 'button');   // upgrade the anchor to a button control
    t.addEventListener('click', (e) => { e.preventDefault(); goto(i); });
    t.addEventListener('keydown', (e) => {
      if (e.key !== ' ' || e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      if (!e.repeat) goto(i);
    });
  });

  // Mobile: track the active frame as the strip is swiped, so the rail ring
  // follows. Desktop drives active from click state instead.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (desktop()) return;
      entries.forEach((en) => {
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          const i = slides.indexOf(en.target);
          if (i > -1) setActive(i);
        }
      });
    }, { root: strip, threshold: 0.5 });
    slides.forEach((s) => io.observe(s));
  }

  // P3 item 3 — hover-zoom, desktop pointers only (never touch). Magnify in
  // place off the high-res source; drop the responsive srcset so the browser
  // actually paints the 2048 source at scale, not a soft upscale of the 1200.
  if (canHover) {
    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      let hires = false;
      slide.addEventListener('pointerenter', () => {
        if (!desktop()) return;
        if (!hires && img.dataset.wsZoomSrc) {
          img.removeAttribute('srcset');
          img.src = img.dataset.wsZoomSrc;
          hires = true;
        }
        img.style.transform = 'scale(1.8)';
      });
      slide.addEventListener('pointermove', (e) => {
        if (!desktop() || !img.style.transform) return;
        const r = slide.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      });
      slide.addEventListener('pointerleave', () => {
        img.style.transform = '';
        img.style.transformOrigin = '';
      });
    });
  }

  // P3 item 5 — on a swatch change, move the gallery to the variant's featured
  // frame (Hollis: closes the "did my pick register?" gap). variantSync writes
  // the chosen variant onto the select and fires 'change'; we read the featured
  // position stashed on the option. Guarded, so products with no media->variant
  // association simply do not move (Tier-1 all-frames fallback).
  const select = document.querySelector('[data-ws-variant-select]');
  select?.addEventListener('change', () => {
    const opt = select.selectedOptions[0];
    const pos = parseInt(opt?.dataset.featuredPos || '', 10);
    if (pos > 0 && pos <= slides.length) goto(pos - 1);
  });

  setActive(0);
  host.classList.add('is-ready');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => host.classList.add('ws-gallery--settled'));
  });
})();

/* ------------------------------------------------------ card colour preview */
/* Hovering a colour chip on a grid card previews that colour by swapping the
   card's main image. Event-delegated: two document listeners cover the whole
   grid, no per-card listeners (R-01 budget). The chips are real links, so mouse,
   touch and no-JS all reach the colour's PDP already; this only layers the hover
   preview on top. The swap is instant (no cross-fade), which is reduced-motion
   safe by construction, so no media query is needed here. */
(function cardSwatchPreview() {
  const pick = (t) => (t && t.closest ? t.closest('.ws-cardswatch') : null);

  document.addEventListener('pointerover', (e) => {
    const sw = pick(e.target);
    if (!sw) return;
    const src = sw.getAttribute('data-swatch-img');
    const card = sw.closest('.ws-card');
    const img = card && card.querySelector('.ws-card__img');
    if (!img || !src) return;
    // Swap via srcset (one attribute to stash/restore): a bare candidate wins
    // over the responsive set, and the original src stays as the fallback.
    if (img.dataset.origset == null) img.dataset.origset = img.getAttribute('srcset') || '';
    img.srcset = src;
    card.classList.add('ws-card--swatching');
  });

  document.addEventListener('pointerout', (e) => {
    const sw = pick(e.target);
    if (!sw) return;
    // Moving between two chips of the same card is not a leave — keep the swap.
    const to = pick(e.relatedTarget);
    const card = sw.closest('.ws-card');
    if (to && to.closest('.ws-card') === card) return;
    const img = card && card.querySelector('.ws-card__img');
    if (img && img.dataset.origset != null) img.srcset = img.dataset.origset;
    if (card) card.classList.remove('ws-card--swatching');
  });
})();

/* ------------------------------------------------------------- variant sync */
/* The <select> is the real control and works with JS disabled. This mirrors the
   button UI onto it, keeps the price and availability honest, and marks
   unavailable combinations rather than hiding them — a struck-through size tells
   the shopper something; a missing one just confuses. */
(function variantSync() {
  const form = document.querySelector('[data-ws-variant-select]')?.closest('form');
  if (!form) return;

  document.documentElement.classList.add('js');

  const select = form.querySelector('[data-ws-variant-select]');
  const radios = [...form.querySelectorAll('[data-ws-option-index]')];
  if (!radios.length) return;

  const options = [...select.options];

  const chosen = () => {
    const byIndex = {};
    radios.filter((r) => r.checked).forEach((r) => {
      byIndex[r.dataset.wsOptionIndex] = r.value;
    });
    return Object.keys(byIndex).sort().map((k) => byIndex[k]);
  };

  const sync = () => {
    const want = chosen().join(' / ');
    const match = options.find((o) => o.textContent.trim().replace(/ — sold out$/, '') === want);
    if (!match) return;

    select.value = match.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // Price
    const price = form.closest('.ws-buybox')?.querySelector('.ws-buybox__price');
    if (price && match.dataset.price) price.textContent = match.dataset.price;

    // Add-to-cart state
    const atc = form.querySelector('[data-ws-atc]');
    if (atc) {
      atc.disabled = match.disabled;
      atc.textContent = match.disabled ? 'Sold out' : 'Add to cart';
    }

    // Echo the chosen value back into the legend ("Size: Large")
    radios.filter((r) => r.checked).forEach((r) => {
      const label = form.querySelector(`[data-ws-optvalue="${r.dataset.wsOptionIndex}"]`);
      if (label) label.textContent = r.value;
    });
  };

  radios.forEach((r) => r.addEventListener('change', sync));
  sync();
})();

/* --------------------------------------------------------------- sticky ATC */
/* Appears once the primary button leaves the viewport. Hides whenever focus is
   inside the buy box, so it can never cover the control someone is using
   (WCAG 2.2 Focus Not Obscured). Tapping it submits the real form — the bar is
   a shortcut to the button, never a second source of truth. */
(function stickyAtc() {
  const bar = document.querySelector('[data-ws-sticky-atc]');
  const form = document.querySelector('[data-ws-variant-select]')?.closest('form');
  const primary = form?.querySelector('[data-ws-atc]');
  if (!bar || !form || !primary) return;

  const buybox = form.closest('.ws-buybox');
  const price = bar.querySelector('[data-ws-sticky-price]');
  const variant = bar.querySelector('[data-ws-sticky-variant]');
  const select = form.querySelector('[data-ws-variant-select]');
  const addBtn = bar.querySelector('[data-ws-sticky-add]');

  let offScreen = false;

  const render = () => {
    const focusInside = buybox?.contains(document.activeElement) || document.activeElement?.matches('[data-ws-thumb], .ws-gallery__slide img[data-ws-zoom-src]');
    // P3 item 6 — a sold-out variant used to make the bar VANISH mid-scroll
    // (was `&& !primary.disabled`), which is disorienting and forfeits recovery.
    // Keep the bar VISIBLE and mirror the main button: disabled + "Sold out".
    // (No "Notify me" — there is no back-in-stock mechanism to promise.)
    let show = offScreen && !focusInside;
    bar.hidden = !show;
    if (show && !bar.contains(document.activeElement)) {
      const headerHeight = document.getElementById('header-group')?.getBoundingClientRect().height || 0;
      const floatingHeight = Math.max(0, ...Array.from(document.querySelectorAll('[data-ws-crew-launcher]'), (widget) => {
        const style = getComputedStyle(widget);
        return widget.hidden || style.display === 'none' || !['fixed', 'sticky'].includes(style.position) ? 0
          : widget.getBoundingClientRect().height + Math.max(0, parseFloat(style.bottom) || 0);
      }));
      show = window.innerHeight >= headerHeight + bar.getBoundingClientRect().height + floatingHeight + 24;
      bar.hidden = !show;
    }
    if (addBtn) {
      addBtn.disabled = primary.disabled;
      addBtn.textContent = primary.disabled ? 'Sold out' : 'Add to cart';
    }
    // Reserve the bar's height so nothing sits underneath it at page end.
    document.body.style.paddingBottom = show ? `${bar.offsetHeight}px` : '';
  };

  new IntersectionObserver(([entry]) => {
    offScreen = !entry.isIntersecting;
    render();
  }, { rootMargin: '0px 0px -80px 0px' }).observe(primary);

  window.addEventListener('resize', render, { passive: true });
  const sizeObserver = new ResizeObserver(render);
  [bar, document.getElementById('header-group'), ...document.querySelectorAll('[data-ws-crew-launcher]')]
    .filter(Boolean).forEach((element) => sizeObserver.observe(element));

  document.addEventListener('focusin', render);
  document.addEventListener('focusout', () => setTimeout(render, 0));

  select?.addEventListener('change', () => {
    const opt = select.options[select.selectedIndex];
    if (!opt) return;
    if (price && opt.dataset.price) price.textContent = opt.dataset.price;
    if (variant) variant.textContent = opt.textContent.replace(/ — sold out$/, '').trim();
    render();
  });

  addBtn?.addEventListener('click', () => {
    if (primary.disabled) return;
    // P3 item 7 — the sticky button puppets the primary buy-box button, so every
    // sticky add otherwise logs as `buy_box` and Marlow cannot measure the sticky
    // bar's contribution to the 1.8% CVR gate. Set a one-tick flag on the puppet
    // path; the add-source publisher reads and clears it to emit `sticky_bar`.
    primary.dataset.stickySource = '1';
    primary.click();
  });
})();

/* ------------------------------------------------------------ hero ambient */
/* The poster is the LCP element and always paints first. Video attaches only
   after load, and never under reduced motion or save-data. */
(function heroVideo() {
  const initialize = (root) => {
    root.querySelectorAll('template[data-ws-hero-media]').forEach((template) => {
      const content = template.content.cloneNode(true);
      const image = content.querySelector('.ws-hero__img');
      const video = content.querySelector('.ws-hero__video');
      const posters = (image?.getAttribute('data-ws-hero-posters') || '').split('|').filter(Boolean);
      const videos = (video?.getAttribute('data-ws-hero-videos') || '').split('|').filter(Boolean);
      if (posters.length > 1) {
        let last = -1;
        try { last = parseInt(sessionStorage.getItem('wsHeroRot'), 10); } catch (e) {}
        let index = Math.floor(Math.random() * posters.length), guard = 0;
        while (index === last && guard++ < 8) index = Math.floor(Math.random() * posters.length);
        try { sessionStorage.setItem('wsHeroRot', String(index)); } catch (e) {}
        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'image';
        preload.href = posters[index];
        preload.setAttribute('fetchpriority', 'high');
        if (![...document.querySelectorAll('link[rel="preload"][as="image"]')].some((link) => link.href === preload.href)) {
          document.head.appendChild(preload);
        }
        image.src = posters[index];
        if (video) {
          if (videos[index]) video.setAttribute('data-ws-hero-video', videos[index]);
          video.setAttribute('poster', posters[index]);
        }
      }
      template.replaceWith(content);
    });
    root.querySelectorAll('[data-ws-hero-video]').forEach((video) => {
      const attach = () => {
        if (!video.isConnected || video.hasAttribute('src')) return;
        if (reduceMotion.matches || navigator.connection?.saveData) return;
        const src = video.getAttribute('data-ws-hero-video');
        if (!src) return;
        video.src = src;
        video.play()
          .then(() => { if (video.isConnected) video.setAttribute('data-ws-playing', 'true'); })
          .catch(() => {});
      };
      if (document.readyState === 'complete') attach();
      else window.addEventListener('load', attach, { once: true });
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();


/* ------------------------------------------------------------------ ws-cart */
/* Add to cart, quantity and removal without leaving the page.

   THE BASE LAYER ALREADY WORKED. Every cart control is a real <form> that posts
   to /cart and renders a real page (ws-cart-contents.liquid). Nothing below is
   required for the cart to function — with JS off, the forms submit normally.
   Once an enhanced request starts, an uncertain result returns to the cart
   without replaying a write that might already have succeeded.

   What was missing was the enhancement: adding to cart navigated away from the
   product, which loses the shopper's place in a collection and is the single
   most common reason a browsing session ends early.

   The drawer is refreshed by fetching /cart and lifting its contents, rather
   than by the Section Rendering API. Both surfaces render the SAME shared
   snippet, so the markup is identical by construction and cannot drift — which
   was the whole point of sharing it. */
(function cart() {
  const drawer = document.getElementById('ws-cart-drawer');
  if (!drawer) return;

  const panel = drawer.querySelector('.ws-drawer__panel');
  const openDrawer = () => drawer.open?.();

  const live = (msg) => {
    let el = document.getElementById('ws-cart-live');
    if (!el) {
      el = document.createElement('p');
      el.id = 'ws-cart-live';
      el.className = 'ws-visually-hidden';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = msg;
  };

  // Serialize writes and their refreshes so an older response cannot replace
  // a newer cart. Recovery cancels queued work instead of replaying uncertain writes.
  let cartQueue = Promise.resolve();
  let cartPending = 0;
  let cartRecovering = false;
  const pendingAdds = new WeakSet();

  const reflectCartBusy = () => {
    const busy = cartPending > 0 || cartRecovering;
    document.querySelectorAll('[data-ws-cart-form]').forEach((form) => {
      if (busy) form.setAttribute('aria-busy', 'true');
      else form.removeAttribute('aria-busy');
    });
    document.querySelectorAll('[data-ws-cart-form] [name="checkout"]').forEach((button) => {
      if (busy && !button.disabled) {
        button.disabled = true;
        button.setAttribute('data-ws-cart-busy-disabled', '');
      } else if (!busy && button.hasAttribute('data-ws-cart-busy-disabled')) {
        button.disabled = false;
        button.removeAttribute('data-ws-cart-busy-disabled');
      }
    });
  };

  const recoverCart = () => {
    cartRecovering = true;
    reflectCartBusy();
    window.location.href = '/cart';
  };

  const queueCartWrite = (work) => {
    if (cartRecovering) return Promise.resolve();
    cartPending += 1;
    reflectCartBusy();
    const run = cartQueue.then(async () => {
      if (cartRecovering) return;
      try { await work(); } catch { recoverCart(); }
    });
    cartQueue = run.catch(() => {});
    return run.finally(() => {
      cartPending -= 1;
      reflectCartBusy();
    });
  };

  // Pull the freshest cart markup and swap it into both surfaces.
  //
  // The COUNT comes from /cart.js, not from the HTML. Counting rendered line
  // rows gives the number of distinct variants, not the number of items — a
  // cart holding one line of three shirts reported "1", and an earlier version
  // of this reported a stale figure while the cart itself was correct. A header
  // badge that lies about the cart is worse than no badge.
  async function refresh() {
    const [markup, state] = await Promise.all([
      fetch('/cart', { headers: { 'X-Requested-With': 'fetch' } }).then((r) => {
        if (!r.ok) throw new Error('Cart markup unavailable');
        return r.text();
      }),
      fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => {
        if (!r.ok) throw new Error('Cart state unavailable');
        return r.json();
      }),
    ]);

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const current = Array.from(document.querySelectorAll('[data-ws-cart-form], .ws-cart__empty'));
    const sources = Array.from(doc.querySelectorAll('[data-ws-cart-form], .ws-cart__empty'));
    if (!current.length || !Number.isInteger(state?.item_count) || state.item_count < 0) {
      throw new Error('Cart refresh incomplete');
    }
    const surfaces = current.map((el) => el.getAttribute('data-ws-cart-surface'));
    if (new Set(surfaces).size !== current.length) throw new Error('Cart surfaces duplicated');
    const replacements = current.map((el, index) => {
      const surface = surfaces[index];
      if (surface !== 'page' && surface !== 'drawer') throw new Error('Cart surface missing');
      const matches = sources.filter((next) => next.getAttribute('data-ws-cart-surface') === surface);
      if (matches.length !== 1) throw new Error('Cart surface response incomplete');
      return { el, next: matches[0] };
    });

    // Keep each surface's own IDs, labels, typed note and disclosure state.
    // Validate every match before replacing either surface.
    replacements.forEach(({ el, next }) => {
      const typed = el.querySelector('[name="note"]')?.value ?? null;
      const noteWasOpen = !!el.querySelector('.ws-cart__note[open]');
      const replacement = next.cloneNode(true);
      el.replaceWith(replacement);
      const note = replacement.querySelector('[name="note"]');
      if (typed !== null && note) note.value = typed;
      if (noteWasOpen) replacement.querySelector('.ws-cart__note')?.setAttribute('open', '');
    });

    const count = state.item_count;
    const head = drawer.querySelector('.ws-drawer__head .ws-micro');
    const badge = document.querySelector('.ws-header__count');
    if (head) head.textContent = count > 0 ? `Aboard · ${count}` : 'Aboard';
    if (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    }
    // The icon's accessible name is the only place the count is spoken.
    const label = document.querySelector('[data-ws-drawer-open="ws-cart-drawer"] .ws-visually-hidden');
    if (label) label.textContent = `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
    reflectCartBusy();
    loadRecs();
    return count;
  }

  // "You may also like" — lazy-load Shopify related-product recommendations into
  // the drawer's placeholder. The placeholder ships inside [data-ws-cart-form], so
  // refresh() re-inserts a fresh, unloaded one, seeded with the id of the most
  // recently added product. Rendered by section 'ws-cart-recommendations' via the
  // Product Recommendations API. Fails silent — the cart is whole without it.
  async function loadRecs() {
    const holder = document.querySelector('#ws-cart-drawer [data-ws-cart-recs]');
    if (!holder || holder.dataset.loaded) return;
    const pid = holder.getAttribute('data-product-id');
    if (!pid) return;
    holder.dataset.loaded = '1';
    try {
      const url = `/recommendations/products?section_id=ws-cart-recommendations&product_id=${encodeURIComponent(pid)}&limit=2&intent=related`;
      const html = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.ws-cart-recs');
      if (content) holder.replaceChildren(content);
    } catch { /* no upsell is fine */ }
  }

  // Add to cart. Capture this submit's variant/options before queued work runs.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (form.matches('[data-ws-cart-form]')) {
      if (cartPending > 0 || cartRecovering) {
        e.preventDefault();
        live('Updating your cart. Please wait before checking out.');
      } else {
        clearTimeout(noteTimer); // the native cart form carries the current note
      }
      return;
    }
    if (!form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();
    if (pendingAdds.has(form) || cartRecovering) return;
    pendingAdds.add(form);
    const button = form.querySelector('[type="submit"]');
    button?.setAttribute('data-loading', 'true');

    try {
      const data = new FormData(form);
      await queueCartWrite(async () => {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: data,
        });
        if (!res.ok) {
          // A stock-limited response may still have added the available quantity.
          const err = await res.json().catch(() => ({}));
          const msg = err.description || err.message || 'We could not confirm the cart update. Please review your cart.';
          let note = form.querySelector('.ws-error');
          if (!note) {
            note = document.createElement('p');
            note.className = 'ws-error';
            note.setAttribute('role', 'alert');
            form.appendChild(note);
          }
          note.textContent = msg;
          await refresh();
          live(msg);
          return;
        }
        form.querySelector('.ws-error')?.remove();
        const count = await refresh();
        live(`Added. ${count} in your cart.`);
        openDrawer();
      });
    } catch {
      recoverCart();
    } finally {
      pendingAdds.delete(form);
      button?.removeAttribute('data-loading');
    }
  });

  // THE GIFT NOTE HAS TO BE SAVED, NOT JUST TYPED.
  //
  // Checkout is a submit button inside the cart form, so with JS off the note
  // rides along with the form and reaches the order. With JS on it did not: any
  // quantity change calls refresh(), refresh() replaces the form markup, and a
  // note typed but not yet submitted was silently destroyed. Write a message,
  // adjust a quantity, lose the message.
  //
  // Persisting it to the cart on change fixes that and does something better —
  // the note survives navigating away and coming back, which is how people
  // actually shop.
  let noteTimer;
  const saveNote = (value) => {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      queueCartWrite(async () => {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
        });
        if (!res.ok) throw new Error('Cart note update unavailable');
      });
    }, 400);
  };

  document.addEventListener('input', (e) => {
    if (e.target.matches?.('[data-ws-cart-form] [name="note"]')) {
      const value = e.target.value;
      document.querySelectorAll('[data-ws-cart-form] [name="note"]').forEach((note) => {
        if (note !== e.target) note.value = value;
      });
      saveNote(value);
    }
  });

  // Removal — a real link, intercepted.
  document.addEventListener('click', async (e) => {
    const el = e.target.closest('[data-ws-cart-remove]');
    if (!el) return;
    e.preventDefault();
    await change(el.getAttribute('data-key'), 0);
  });

  // Quantity — commits when the field is left or Enter is pressed. There are no
  // stepper buttons to hook: the control is a real number input, and the only
  // submit path used to be the button inside <noscript>, so with JS ON changing
  // a quantity did nothing whatsoever.
  document.addEventListener('change', async (e) => {
    const input = e.target;
    if (!input.matches('[name="updates[]"][data-line]')) return;
    const qty = Math.max(0, Number(input.value || 0));
    await change(input.getAttribute('data-key'), qty);
  });

  async function change(key, quantity) {
    // A removed row shifts every following line number. Target the exact key;
    // if it has changed or disappeared, recover instead of using another row.
    if (!key || !Number.isInteger(quantity)) return recoverCart();
    await queueCartWrite(async () => {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('change failed');
      const count = await refresh();
      live(quantity === 0 ? `Removed. ${count} in your cart.` : `Updated. ${count} in your cart.`);
      if (drawer.querySelector('dialog')?.open && panel) panel.focus();
    });
  }

  // Populate recommendations when the shopper opens the drawer from the header
  // with items already aboard (no add/refresh has run this session).
  document
    .querySelector('[data-ws-drawer-open="ws-cart-drawer"]')
    ?.addEventListener('click', () => { loadRecs(); });

  // Recommended additions share the same queue and duplicate-submit guard.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest?.('[data-ws-rec-add]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.getAttribute('data-variant-id');
    if (!id || pendingAdds.has(btn) || cartRecovering) return;
    pendingAdds.add(btn);
    btn.setAttribute('data-loading', 'true');
    try {
      await queueCartWrite(async () => {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(id), quantity: 1 }),
        });
        if (!res.ok) throw new Error('add failed');
        const count = await refresh();
        live(`Added. ${count} in your cart.`);
      });
    } finally {
      pendingAdds.delete(btn);
      btn.removeAttribute('data-loading');
    }
  });
})();


/* ----------------------------------------------------------- ws-form-focus */
/* Move focus to the outcome after a form round-trip.

   Shopify's classic forms POST and re-render the whole page. A sighted user
   sees the green confirmation immediately; a keyboard or screen-reader user is
   returned to the very top of the document with no idea whether anything
   happened, and has to hunt back down through the header and nav to find out.

   The markup already carried `data-ws-focus` on the contact form's success
   message — it had simply never been implemented, so the attribute sat there
   doing nothing. This implements it, and the same treatment is given to error
   states, which matter more: that is the case where the reader most needs to
   know what went wrong and where.

   The element must be focusable without being in the tab order, hence
   tabindex="-1" in the markup. */
(function formFocus() {
  const target = document.querySelector('[data-ws-focus]');
  if (!target) return;
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  // After paint, so the browser's own scroll restoration does not fight it.
  requestAnimationFrame(() => {
    target.focus({ preventScroll: false });
  });
})();


/* -------------------------------------------------------- ws-search-drawer */
/* Predictive search. Extends WSDrawer, so focus trapping, Esc, the scroll lock
   and the explicit teardown all come along unchanged.

   The base layer is untouched: the header control is a real link to /search and
   the form inside posts there too, so Enter always works whether or not a
   suggestion has arrived. This only makes the answer faster.

   Shopify's /search/suggest.json is used rather than the Section Rendering API:
   it returns the three groups already separated, so there is nothing to parse
   out of an HTML blob. */
class WSSearchDrawer extends WSDrawer {
  connectedCallback() {
    super.connectedCallback();
    this.input = this.querySelector('[data-ws-search-input]');
    this.results = this.querySelector('[data-ws-search-results]');
    this.status = this.querySelector('[data-ws-search-status]');
    if (!this.input || !this.results) return;

    this.requestVersion = 0;
    this.listbox = document.createElement('div');
    this.listbox.id = `${this.results.id}-listbox`;
    this.listbox.setAttribute('role', 'listbox');
    const label = this.input.labels?.[0];
    if (label) {
      if (!label.id) label.id = `${this.input.id}-label`;
      this.listbox.setAttribute('aria-labelledby', label.id);
    }
    this.results.replaceChildren(this.listbox);
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-controls', this.listbox.id);
    this.input.setAttribute('autocorrect', 'off');
    this.input.setAttribute('autocapitalize', 'off');
    this.input.setAttribute('spellcheck', 'false');
    this.clear();

    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      this.clear();
      const q = this.input.value.trim();
      if (q.length < 2) return;
      timer = setTimeout(() => this.search(q), 220);
    });
    this.input.addEventListener('blur', () => this.select(-1));

    this.addEventListener('keydown', (e) => {
      // Composition keys belong to the input method, including boundary keydowns.
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === 'Tab' && !e.altKey && !e.ctrlKey && !e.metaKey && this.dialog.open) {
        const stops = [...this.dialog.querySelectorAll('button, input, a[href], [tabindex]')]
          .filter((node) => node.tabIndex >= 0 && !node.matches(':disabled') && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
        const edge = e.shiftKey ? stops[0] : stops[stops.length - 1];
        if (e.target === edge) {
          e.preventDefault();
          (e.shiftKey ? stops[stops.length - 1] : stops[0])?.focus();
        }
        return;
      }
      if (e.target !== this.input) return;
      if (e.key === 'Escape' && !this.listbox.hidden) {
        e.preventDefault();
        e.stopPropagation();
        this.input.value = '';
        this.clear();
        return;
      }
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) this.select(-1);
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const links = [...this.listbox.querySelectorAll('.ws-searchd__link')];
      if (!links.length) return;
      const at = links.findIndex((l) => l.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') {
        if (at > -1) { e.preventDefault(); links[at].click(); }
        return;
      }
      e.preventDefault();
      const next = e.key === 'ArrowDown'
        ? Math.min(at + 1, links.length - 1)
        : at < 0 ? links.length - 1 : at - 1;
      this.select(next);
    });
  }

  select(index) {
    const links = [...(this.listbox?.querySelectorAll('.ws-searchd__link') || [])];
    links.forEach((link, i) => {
      link.setAttribute('aria-selected', i === index ? 'true' : 'false');
      if (i === index) link.setAttribute('data-active', 'true');
      else link.removeAttribute('data-active');
    });
    this.input?.removeAttribute('aria-activedescendant');
    if (links[index]) {
      this.input.setAttribute('aria-activedescendant', links[index].id);
      links[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }
  }

  open() {
    super.open();
    requestAnimationFrame(() => {
      if (!this.dialog.open) return;
      this.input?.focus();
      const q = this.input?.value.trim();
      if (q?.length >= 2) this.search(q);
    });
  }

  teardown() {
    super.teardown();
    this.clear();
  }

  clear() {
    this.requestVersion += 1;
    this.select(-1);
    if (this.listbox) { this.listbox.replaceChildren(); this.listbox.hidden = true; }
    this.results?.querySelector('[data-ws-search-footer]')?.remove();
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-busy');
    if (this.status) this.status.textContent = '';
  }

  async search(q) {
    if (!this.dialog.open) return;
    const version = ++this.requestVersion;
    this.input.setAttribute('aria-busy', 'true');
    const url = '/search/suggest.json'
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=product,page,article'
      + '&resources[limit]=5';
    let data;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Search response unavailable');
      data = (await response.json()).resources.results;
    } catch {
      if (version === this.requestVersion) this.clear();
      return;
    }
    if (version !== this.requestVersion || this.input.value.trim() !== q || !this.dialog.open) return;

    const esc = (s) => String(s).replace(/[&<>"']/g, (m) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
    const thumb = (url) => url + (url.includes('?') ? '&' : '?') + 'width=88';
    let optionIndex = 0, groupIndex = 0;
    const group = (label, items, withThumb) => {
      if (!items || !items.length) return '';
      const groupId = `${this.listbox.id}-group-${groupIndex++}`;
      return `<div class="ws-searchd__group" role="group" aria-labelledby="${groupId}">
        <p class="ws-micro ws-searchd__grouphead" id="${groupId}">${label}</p>
        <ul class="ws-searchd__list" role="presentation">
          ${items.map((i) => `<li class="ws-searchd__item" role="presentation">
            <a class="ws-searchd__link" href="${esc(i.url)}" id="${this.listbox.id}-option-${optionIndex++}" role="option" aria-selected="false" tabindex="-1">
              ${withThumb && i.image ? `<img class="ws-searchd__thumb" src="${esc(thumb(i.image))}" alt="" width="44" height="55" loading="eager" decoding="async">` : ''}
              <span class="ws-searchd__title">${esc(i.title)}</span>
              ${i.price ? `<span class="ws-small ws-searchd__price">${esc(Shopify.formatMoney ? Shopify.formatMoney(i.price * 100) : '$' + Math.round(i.price))}</span>` : ''}
            </a></li>`).join('')}
        </ul></div>`;
    };

    const total = (data.products?.length || 0) + (data.pages?.length || 0) + (data.articles?.length || 0);
    this.select(-1);
    this.listbox.innerHTML = group('Products', data.products, true)
      + group('Pages', data.pages, false)
      + group('The Log', data.articles, true);
    this.listbox.hidden = !total;
    this.results.querySelector('[data-ws-search-footer]')?.remove();
    this.results.insertAdjacentHTML('beforeend', total
      ? `<p class="ws-small ws-searchd__all" data-ws-search-footer><a href="/search?q=${encodeURIComponent(q)}">See all results for &ldquo;${esc(q)}&rdquo;</a></p>`
      : `<p class="ws-body ws-searchd__empty" data-ws-search-footer>Nothing surfaced for &ldquo;${esc(q)}&rdquo;. Try a symbol — Sketchy, Bear, Script, Ahoy, Anchor.</p>`);

    this.input.setAttribute('aria-expanded', total ? 'true' : 'false');
    this.input.removeAttribute('aria-busy');
    if (this.status) {
      this.status.textContent = total
        ? `${total} result${total === 1 ? '' : 's'} for ${q}`
        : `No results for ${q}`;
    }
  }
}
customElements.define('ws-search-drawer', WSSearchDrawer);


/* ----------------------------------------------------------- ws-analytics */
/* The events only the theme can know.
 *
 * NO DOUBLE-FIRING (measurement-plan §3). Shopify already emits the standard
 * funnel — view_item, add_to_cart, search, begin_checkout, purchase — from its
 * own pixel sandbox, and those stay the source of truth. Re-emitting them from
 * the page would inflate every number in the report.
 *
 * What Shopify cannot know is intent: WHICH option was chosen, WHERE an add
 * came from, WHETHER the delivery window was actually on screen. Those are
 * published as custom events with a `ws_` prefix so they can never collide with
 * a standard name, through Shopify.analytics.publish so they arrive in Customer
 * Events where any pixel can subscribe — rather than into a dataLayer whose six
 * Google containers nobody has audited yet.
 *
 * Fails silently and completely. Analytics must never break a shop.
 */
(function analytics() {
  const publish = (name, payload) => {
    try { window.Shopify?.analytics?.publish?.(name, payload || {}); } catch { /* never break the page */ }
  };

  const productId = () => document.querySelector('[data-ws-product-id]')?.getAttribute('data-ws-product-id') || null;

  // Which option was chosen — Shopify sees the variant, not the decision.
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el.matches?.('[data-ws-variant-select], .ws-buybox__form select[name="id"]')) {
      publish('ws_select_variant', {
        item_id: productId(),
        variant_id: el.value,
        option_value: el.options?.[el.selectedIndex]?.text || null,
      });
    }
    if (el.matches?.('[name="updates[]"][data-line]')) {
      publish('ws_cart_quantity_change', { line: el.getAttribute('data-line'), quantity: el.value });
    }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;

    // Where an add actually came from: buy box, sticky bar, or a cross-sell.
    // The sticky bar puppets the primary button, so it can't be told apart by
    // DOM position — it tags the puppet path with a one-tick dataset flag
    // (P3 item 7). Read and clear that first; fall back to structure otherwise.
    const addBtn = t.closest?.('form[action*="/cart/add"] [type="submit"]');
    if (addBtn) {
      let where;
      if (addBtn.dataset.stickySource) {
        where = 'sticky_bar';
        delete addBtn.dataset.stickySource;
      } else if (addBtn.closest('.ws-pairs')) {
        where = 'pairs_well';
      } else {
        where = 'buy_box';
      }
      publish('ws_add_to_cart_source', { item_id: productId(), source: where });
    }

    if (t.closest?.('[data-ws-size-guide], a[href*="size-guide"], a[href*="sizing"]')) {
      publish('ws_open_size_guide', { item_id: productId(), source: 'pdp' });
    }

    const result = t.closest?.('.ws-searchd__link');
    if (result) {
      const links = [...document.querySelectorAll('.ws-searchd__link')];
      publish('ws_select_search_result', {
        term: document.querySelector('[data-ws-search-input]')?.value || null,
        position: links.indexOf(result) + 1,
        destination: result.getAttribute('href'),
      });
    }

    const campaignCta = t.closest?.('[data-ws-campaign-cta]');
    if (campaignCta) {
      publish('ws_campaign_cta', {
        campaign_id: campaignCta.getAttribute('data-ws-campaign-cta'),
        destination: campaignCta.getAttribute('href'),
      });
    }
  });

  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f.matches?.('form[action*="/contact"]')) return;
    // Placement matters more than the count: it tells us which surface earns them.
    const placement = f.closest('.ws-footer') ? 'footer'
      : f.closest('.ws-email') ? 'inline'
      : 'other';
    publish('ws_email_signup', { placement });
  });

  // Seen, not merely rendered — both of these only mean something if they were
  // actually on screen. One observer, disconnected after it fires.
  const seen = (selector, name, payload) => {
    const el = document.querySelector(selector);
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((x) => x.isIntersecting)) return;
      publish(name, typeof payload === 'function' ? payload(el) : payload);
      io.disconnect();
    }, { threshold: 0.5 });
    io.observe(el);
  };

  seen('.ws-buybox__deliveryline', 'ws_delivery_date_view', (el) => ({
    item_id: productId(),
    window: el.textContent.replace(/\s+/g, ' ').trim(),
  }));
  seen('.ws-impact-receipt', 'ws_impact_view', () => ({
    surface: document.querySelector('#ws-cart-drawer .ws-impact-receipt') ? 'cart_receipt' : 'band',
  }));
})();

/* ------------------------------------------------------ ws-provenance-seal */
/* The Provenance Seal's inspect panel — "How this image was made" (DEC-147).
   Pure enhancement: the seal glyph + always-visible mono word are server-
   rendered and satisfy the disclosure floor with JS off. This only upgrades the
   PDP seal button to open the WS-composed panel (never the raw Adobe popover).
   The panel is a non-modal role="dialog"; Esc, the close button, an outside
   click, and (on mobile) the scrim all dismiss it, and focus returns to the
   seal. Reduced-motion is handled in CSS via the shared duration tokens. */
class WSProvenanceSeal extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.ws-seal--pdp');
    this.panel = this.querySelector('.ws-seal__panel');
    this.scrim = this.querySelector('[data-ws-seal-scrim]');
    if (!this.button || !this.panel) return;

    this.close = this.close.bind(this);
    this.onDocKeydown = this.onDocKeydown.bind(this);
    this.onDocPointer = this.onDocPointer.bind(this);

    this.button.addEventListener('click', () => this.toggle());
    this.panel.querySelector('[data-ws-seal-close]')?.addEventListener('click', () => this.close());
    this.scrim?.addEventListener('click', () => this.close());
  }

  toggle() { this.hasAttribute('data-open') ? this.close() : this.open(); }

  open() {
    this.setAttribute('data-open', '');
    this.panel.hidden = false;
    if (this.scrim) this.scrim.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    // Move focus into the panel so keyboard + AT land on the disclosure.
    (this.panel.querySelector('[data-ws-seal-close]') || this.panel).focus?.();
    document.addEventListener('keydown', this.onDocKeydown);
    // Defer so the opening click itself is not read as an outside click.
    setTimeout(() => document.addEventListener('pointerdown', this.onDocPointer), 0);
  }

  close() {
    if (!this.hasAttribute('data-open')) return;
    this.removeAttribute('data-open');
    this.panel.hidden = true;
    if (this.scrim) this.scrim.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('pointerdown', this.onDocPointer);
    this.button.focus();
  }

  onDocKeydown(e) { if (e.key === 'Escape') this.close(); }

  onDocPointer(e) { if (!this.contains(e.target)) this.close(); }
}
customElements.define('ws-provenance-seal', WSProvenanceSeal);

/* ------------------------------------------------------------- ws-shop-nav */
/* "Shop by collection" — the desktop two-axis discovery disclosure. Pure
   enhancement: the trigger is a real <a href="/collections">, so with JS off it
   navigates to the collection index. This upgrades it to a NON-MODAL panel that
   drops under the header lid — no modal, no page-dim, no focus trap. It reuses
   the drawer's ATTRIBUTE CONTRACT (aria-expanded, Esc, outside-click and
   blur-away teardown, idempotent close), the same shape as ws-provenance-seal.
   The button is the source of truth; hover-with-intent is layered on top. Panel
   open/close motion is CSS via the shared duration tokens, so reduced motion is
   handled with no branch here. */
class WSShopNav extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.ws-shop-trigger');
    this.panel = this.querySelector('.ws-shop-panel');
    if (!this.trigger || !this.panel) return;

    this.close = this.close.bind(this);
    this.onDocKeydown = this.onDocKeydown.bind(this);
    this.onDocPointer = this.onDocPointer.bind(this);
    this.canHover = window.matchMedia('(hover: hover)').matches;

    // Button is the source of truth. Intercept the link's navigation and toggle;
    // add Space (a link ignores it) so the upgraded control behaves like a button.
    this.trigger.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); this.toggle(); }
    });

    // Blur-away teardown — closes when keyboard focus leaves the component.
    this.addEventListener('focusout', (e) => {
      if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close(false);
    });

    // Hover-with-intent — enhancement only (skipped on touch). ~150ms to open,
    // a short grace on leave (WCAG 1.4.13 hoverable). The panel is a descendant,
    // so moving onto it does not fire pointerleave.
    if (this.canHover) {
      this.addEventListener('pointerenter', () => {
        clearTimeout(this.leaveTimer);
        this.hoverTimer = setTimeout(() => this.open(), 150);
      });
      this.addEventListener('pointerleave', () => {
        clearTimeout(this.hoverTimer);
        this.leaveTimer = setTimeout(() => this.close(false), 200);
      });
    }
  }

  toggle() { this.hasAttribute('data-open') ? this.close(true) : this.open(); }

  open() {
    if (this.hasAttribute('data-open')) return;
    this.setAttribute('data-open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onDocKeydown);
    // Defer so the opening click itself is not read as an outside click.
    setTimeout(() => document.addEventListener('pointerdown', this.onDocPointer), 0);
  }

  close(refocus) {
    if (!this.hasAttribute('data-open')) return;
    this.removeAttribute('data-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('pointerdown', this.onDocPointer);
    clearTimeout(this.hoverTimer);
    clearTimeout(this.leaveTimer);
    // Return focus to the trigger only for keyboard/explicit dismissals, never
    // on an outside click (which would yank focus to where the user clicked).
    if (refocus) this.trigger.focus();
  }

  onDocKeydown(e) { if (e.key === 'Escape') this.close(true); }

  onDocPointer(e) { if (!this.contains(e.target)) this.close(false); }
}
customElements.define('ws-shop-nav', WSShopNav);

(function labelStoreQualityFrame() {
  const label = document.querySelector('[data-ws-store-quality-title]')?.dataset.wsStoreQualityTitle;
  if (!label) return;
  const labelFrame = (frame) => {
    if (!frame.matches?.('iframe#merchantwidgetiframe') || frame.title.trim()) return;
    let source;
    try { source = new URL(frame.src, document.baseURI); } catch { return; }
    if (source.hostname !== 'www.google.com' || !source.pathname.startsWith('/shopping/merchantverse/')) return;
    frame.title = label;
  };
  const scan = (node) => {
    if (node.nodeType !== 1) return;
    labelFrame(node);
    node.querySelectorAll('iframe#merchantwidgetiframe').forEach(labelFrame);
  };
  scan(document.documentElement);
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') labelFrame(record.target);
      else record.addedNodes.forEach(scan);
    }
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['id', 'src'] });
})();


// Keep floating rewards and store-quality controls above the active mobile buy bar.
(() => {
  const bar = document.querySelector('[data-ws-sticky-atc]');
  if (!bar) return;
  const mobile = window.matchMedia('(max-width: 989px)');
  const originals = new WeakMap();
  const thumbOriginals = new WeakMap();
  const header = document.getElementById('header-group');
  let queued = false;
  const update = () => {
    queued = false;
    const height = mobile.matches && !bar.hidden && getComputedStyle(bar).display !== 'none'
      ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    document.querySelectorAll('[data-ws-crew-launcher], iframe#merchantwidgetiframe').forEach((widget) => {
      const floating = ['fixed', 'sticky'].includes(getComputedStyle(widget).position);
      if (!floating && !originals.has(widget)) return;
      if (!originals.has(widget)) originals.set(widget, {
        value: widget.style.getPropertyValue('margin-bottom'),
        priority: widget.style.getPropertyPriority('margin-bottom'),
        base: parseFloat(getComputedStyle(widget).marginBottom) || 0
      });
      const original = originals.get(widget);
      const value = height && floating ? `${original.base + height}px` : original.value;
      const priority = height && floating ? 'important' : original.priority;
      if (widget.style.getPropertyValue('margin-bottom') === value &&
          widget.style.getPropertyPriority('margin-bottom') === priority) return;
      if (value) widget.style.setProperty('margin-bottom', value, priority);
      else widget.style.removeProperty('margin-bottom');
    });
    const rewards = document.querySelector('[data-ws-crew-launcher]');
    const rewardsHeight = mobile.matches && rewards && !rewards.hidden && getComputedStyle(rewards).display !== 'none' && ['fixed', 'sticky'].includes(getComputedStyle(rewards).position)
      ? Math.max(0, window.innerHeight - rewards.getBoundingClientRect().top) : 0;
    const clearance = mobile.matches && (height || rewardsHeight) ? Math.ceil(Math.max(height, rewardsHeight) + 12) : 0;
    const headerClearance = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position)
      ? Math.ceil(header.getBoundingClientRect().height + 12) : 0;
    document.querySelectorAll('[data-ws-thumb]').forEach((thumb) => {
      if (!thumbOriginals.has(thumb)) thumbOriginals.set(thumb, Object.fromEntries(['top', 'bottom'].map((side) => [side, {
        value: thumb.style.getPropertyValue(`scroll-margin-${side}`),
        priority: thumb.style.getPropertyPriority(`scroll-margin-${side}`),
        base: parseFloat(getComputedStyle(thumb).getPropertyValue(`scroll-margin-${side}`)) || 0
      }])));
      Object.entries({ top: headerClearance, bottom: clearance }).forEach(([side, space]) => {
        const original = thumbOriginals.get(thumb)[side];
        const value = space ? `${Math.max(original.base, space)}px` : original.value;
        const priority = space ? 'important' : original.priority;
        const property = `scroll-margin-${side}`;
        if (thumb.style.getPropertyValue(property) === value && thumb.style.getPropertyPriority(property) === priority) return;
        if (value) thumb.style.setProperty(property, value, priority);
        else thumb.style.removeProperty(property);
      });
    });
    const focused = document.activeElement;
    if (focused?.matches('[data-ws-thumb]')) focused.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };
  new ResizeObserver(schedule).observe(bar);
  if (header) new ResizeObserver(schedule).observe(header);
  new MutationObserver(schedule).observe(bar, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
  new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches('[data-ws-crew-launcher], iframe#merchantwidgetiframe, [data-ws-thumb]') ||
        node.querySelector('[data-ws-crew-launcher], iframe#merchantwidgetiframe, [data-ws-thumb]'))))) schedule();
  }).observe(document.body, { childList: true, subtree: true });
  mobile.addEventListener('change', schedule);
  document.addEventListener('focusin', (event) => {
    if (event.target.matches?.('[data-ws-thumb]')) schedule();
  });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
})();
